import ExcelJS from "exceljs";
import moment from "moment";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateType, DateValueType } from "react-tailwindcss-datepicker";
import { ListFieldsType, MTCheckListCommentsFiles } from "src/api/config";
import {
  myTaskCheckListCommentsFilesListName,
  myTaskCheckListCommentsListName,
  myTaskCheckListConnectionWithTasksListName,
  myTaskCheckListTasksListName,
} from "src/api/config/meetings.config";
import { ClosePanelBtnMobile, DrawerComponent, Modal } from "src/components/DialogComponent";
import {
  getStatus,
  updateTaskStatus,
} from "src/components/tables/meetingTables/meetingsCheckListTable/MyTaskCheckListTable";
import { DownloadWithHoverIcon } from "src/components/ui-icons/upload/DownloadIcon";
import TGButton from "src/components/ui/TGButton";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectCurrentUser, selectUsers } from "src/modules/users/selector";
import { RootState } from "src/store/rootReducer";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { TooltipItem } from "src/utils/Tooltip";
import { twMerge } from "tailwind-merge";
import { TaskFormValues } from ".";
import AddActionInComponent from "../addAction/inComponent";
import TGCheckbox from "./../../../../components/ui/TGCheckbox";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import TGQuestionnaireRHF, { QuestionItem } from "src/components/ui/TGQuestionnaire";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { updateItemSuccess } from "src/modules/spItems/actions";
import { getItemsRequest, updateItemRequest } from "src/modules/spItems/api";
import { Option } from "src/components/ui/TGSelect";
import Loading from "src/components/Loading";
import { AdditionalFieldsProvider } from "../meetingsPage/providers/additionalFieldsProvider";
import CheckListAdditionalMeetingFields from "./CheckListAdditionalMeetingFields";
import { selectJustUpdatedItem } from "src/modules/spItems/selectors";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { myTaskActionsListName } from "src/api/config/myTasksActions.config";
import Comments from "src/modules/comments";
import {
  TaskItem,
  getStartEndDateByPeriod,
  fetchChecklistTaskById,
  selectChecklistTaskById,
  updateChecklistTaskLocally,
} from "src/modules/checklistTasks";
import { getTaskByID } from "src/modules/checklistTasks/api";
import AddAudit from "../addAudit/addAudit.mobile";
import Add5Way from "../add5Way/add5Way.mobile";
import AddIncident from "../addIncident/addIncident.mobile";

export function TaskCard({
  zIndex,
  setOpen,
  open,
  item,
  periodDate,
  changeTask,
  readOnly = false,
  taskID,
  responsibleEmail,
}: {
  item?: TaskItem;
  zIndex: number;
  open: boolean;
  setOpen: (b: boolean) => void;
  periodDate?: DateValueType;
  changeTask?(key: keyof TaskItem, value: any): void;
  readOnly?: boolean;
  taskID?: string | null;
  // Email сотрудника, чей чек-лист открыт (например, при просмотре со страницы "Моя команда").
  // Нужен, т.к. карточка может рендериться без item (только по taskID из URL, после перезагрузки
  // страницы), и тогда у неё нет иного способа узнать, чья это задача, кроме currentUser.email,
  // что ломает загрузку задач подчинённых.
  responsibleEmail?: string | null;
}) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  // const periodDate = useAppSelector(selectChecklistPeriodDate);
  const connectionId = useMemo(() => {
    if (item?.ConnectionID) return Number(item.ConnectionID);
    if (taskID) return Number(taskID);
    return null;
  }, [item?.ConnectionID, taskID]);
  const taskSelector = useMemo(() => selectChecklistTaskById(connectionId ?? undefined), [connectionId]);
  const storeTask = useAppSelector(taskSelector);
  const resolvedTask = item ?? storeTask ?? null;
  const effectivePeriod = useMemo<DateValueType>(() => {
    if (periodDate) {
      return periodDate;
    }
    const today = new Date();
    return { startDate: today, endDate: today };
  }, [periodDate]);
  const [linkCopied, setLinkCopied] = useState(false);

  const fetchKey = useMemo(() => {
    if (!connectionId) return null;
    const start = effectivePeriod?.startDate ? new Date(effectivePeriod.startDate).getTime() : "na";
    const end = effectivePeriod?.endDate ? new Date(effectivePeriod.endDate).getTime() : "na";
    return `${connectionId}_${start}_${end}`;
  }, [connectionId, effectivePeriod?.startDate, effectivePeriod?.endDate]);

  const lastFetchedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!connectionId || !fetchKey) {
      return;
    }

    const alreadyFetched = lastFetchedKeyRef.current === fetchKey;
    if (alreadyFetched && resolvedTask) {
      return;
    }

    lastFetchedKeyRef.current = fetchKey;

    dispatch(
      fetchChecklistTaskById({
        connectionId,
        targetEmail: responsibleEmail || currentUser.email,
        currentUserEmail: currentUser.email,
        periodDate: effectivePeriod,
      }),
    );
  }, [connectionId, currentUser.email, dispatch, effectivePeriod, fetchKey, resolvedTask, responsibleEmail]);

  const effectiveChangeTask = useCallback(
    (key: keyof TaskItem, value: any) => {
      if (changeTask) {
        changeTask(key, value);
        return;
      }
      if (!connectionId) return;
      dispatch(updateChecklistTaskLocally({ connectionId, changes: { [key]: value } as Partial<TaskItem> }));
    },
    [changeTask, connectionId, dispatch],
  );

  const handleCopyLink = useCallback(async () => {
    if (!connectionId) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("taskCard", String(connectionId));
      const ownerEmail = resolvedTask?.ResponsibleEmail || responsibleEmail;
      if (ownerEmail && ownerEmail !== "all") {
        url.searchParams.set("taskCardEmail", ownerEmail);
      } else {
        url.searchParams.delete("taskCardEmail");
      }

      const shareLink = url.toString();
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareLink);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = shareLink;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setLinkCopied(true);
    } catch (error) {
      console.error("Failed to copy checklist task link", error);
      setLinkCopied(false);
    }
  }, [connectionId, resolvedTask?.ResponsibleEmail, responsibleEmail]);

  useEffect(() => {
    if (!linkCopied) return;
    const timeout = setTimeout(() => setLinkCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [linkCopied]);

  useEffect(() => {
    setLinkCopied(false);
  }, [connectionId]);

  return (
    <DrawerComponent percentage="87" zIndex={zIndex} open={open} toggleDrawer={(t) => setOpen(t)}>
      {!resolvedTask ? (
        <FlexCol className="px-7.5 py-5 justify-center items-center gap-5 w-full h-full">
          <span className="text-base text-darkblue">Загружаем задачу...</span>
          <Loading />
        </FlexCol>
      ) : (
        <Content
          toggleOpen={setOpen}
          changeTask={effectiveChangeTask}
          item={resolvedTask}
          periodDate={effectivePeriod}
          readOnly={readOnly}
          currentUser={currentUser}
          onCopyLink={handleCopyLink}
          linkCopied={linkCopied}
          zIndex={zIndex}
        />
      )}
    </DrawerComponent>
  );
}

const getTaskPeriodString = (period: string, date: DateType | undefined) => {
  const { start, end } = getStartEndDateByPeriod(period, date);

  return `${period} · ${moment(start).format("DD.MM.YYYY")} \u2014 ${period === "Неделя" || period === "Месяц" || period === "Квартал" || period === "Год"
      ? moment(end).subtract(1, "day").format("DD.MM.YYYY")
      : moment(end).format("DD.MM.YYYY")
  }`;
};

function CreateTaskRelationsButton({ zIndex, disabled = false }: { zIndex: number; disabled?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="inline-flex relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setIsOpen((prev) => !prev);
        }}
        className={twMerge(
          "inline-flex items-center border font-medium rounded-md px-4 justify-center gap-x-2 text-darkblue bg-white hover:bg-gray-50 shadow-custom text-base lg:px-6 py-3 sm:px-4 w-full",
          disabled && "cursor-not-allowed text-[#8899A8]",
        )}
      >
        <span className="text-lg">Создать</span>
      </button>
      {isOpen && (
        <div className="py-6 px-4 absolute bottom-full right-0 z-30 mb-2 w-full overflow-hidden rounded-md bg-white text-darkblue shadow-custom">
          <h3 className="text-primaryGray">Создать</h3>
          <AddAudit zIndex={zIndex + 20} fromTaskCard />
          <Add5Way zIndex={zIndex + 20} type="FiveWay" fromTaskCard />
          <AddIncident zIndex={zIndex + 20} fromTaskCard />

          <TGButton
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen((prev) => !prev);
              }}
            >
              Отмена
            </TGButton>
        </div>
      )}
    </div>
  );
}

function Content({
  item,
  toggleOpen,
  periodDate,
  changeTask,
  readOnly = false,
  currentUser,
  onCopyLink,
  linkCopied,
  zIndex,
}: {
  item: TaskItem;
  toggleOpen: (b: boolean) => void;
  periodDate: DateValueType;
  changeTask(key: keyof TaskItem, value: any): void;
  readOnly?: boolean;
  currentUser: ReturnType<typeof selectCurrentUser>;
  onCopyLink: () => void;
  linkCopied: boolean;
  zIndex: number;
}) {
  const dispatch = useAppDispatch();
  const { Task, Result, Direction, Period, Test, ResponsibleEmail, AuthorEmail } = item;
  // Является ли текущий пользователь непосредственным руководителем того,
  // на чей чек-лист назначена эта задача (по реальным данным DirectReports из AD/SSO,
  // а не по URL страницы — это исключает возможность подделать права через адресную строку).
  const reportsEmails = useMemo(
    () =>
      new Set(
        (currentUser.reports || []).map((account: string) => {
          const parts = String(account || "").split("|");
          return parts[parts.length - 1].toLowerCase().trim();
        }),
      ),
    [currentUser.reports],
  );
  const normalizedCurrentEmail = String(currentUser.email || "").toLowerCase().trim();
  const normalizedResponsible = String(ResponsibleEmail || "").toLowerCase().trim();
  const normalizedAuthor = String(AuthorEmail || "").toLowerCase().trim();
  const isManagerView = reportsEmails.has(normalizedResponsible);
  // Заполнять/отмечать выполнение задачи может только сам ответственный исполнитель.
  // Руководитель (как и любой другой пользователь, не являющийся исполнителем), открыв
  // чужую задачу — в т.ч. через подделанные параметры страницы — может только её просматривать,
  // т.к. тут используются реальные данные задачи (ResponsibleEmail), а не то, что передано извне.
  const isResponsibleUser =
    normalizedResponsible === normalizedCurrentEmail || (normalizedResponsible === "all" && !isManagerView);
  const effectiveDisabled = readOnly || item.disabled || !isResponsibleUser;
  // Удалять задачу может только её автор (создатель), независимо от того, кто её исполняет
  // и является ли автор руководителем исполнителя.
  const canManageTask = normalizedAuthor === normalizedCurrentEmail;
  const [openCompletedModal, setOpenCompletedModal] = useState(false);
  const [openActionRequiredModal, setOpenActionRequiredModal] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [hasAddedAction, setHasAddedAction] = useState(false);
  // const [tasks, setTasks] = useState<TaskType[]>([]);

  // Parse saved answers once
  const savedAnswers = useMemo(() => {
    try {
      if (!item.Answers) return {};
      if (typeof item.Answers === "string") return JSON.parse(item.Answers);
      if (typeof item.Answers === "object") return item.Answers;
      return {};
    } catch (e) {
      console.error("Failed to parse answers:", e);
      return {};
    }
  }, [item.Answers]);

  // Track if questionnaire has unsaved changes (for isDone tasks)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  // RHF form for questionnaire
  const form = useForm<{ progressPercent: number; answers: Record<string, unknown> } | any>({
    defaultValues: {
      progressPercent: 0,
      answers: {},
    },
    disabled: readOnly,
    mode: "onChange",
  });

  // Watch progress from RHF instead of local state
  const progressWatch = useWatch({ control: form.control, name: "progressPercent" }) as number | undefined;

  // Reset state when item/answers change (new task opened or data refreshed)
  useEffect(() => {
    setHasUnsavedChanges(false);
    setResetKey((prev) => prev + 1);
  }, [item.ID, item.Answers, item.Result.isDone, periodDate?.startDate, item.Result.hasDeviation]);

  function checkActionsByQuestion(): boolean {
    const answers = form.getValues("answers") as Record<string, unknown>;
    const filtered: Array<{ questionKey: string; options: any[] }> = [];

    for (const [key, value] of Object.entries(answers)) {
      if (key.startsWith("single_")) {
        const v = value as any;
        const opt = v?.value ?? v?.rest?.value;
        if (opt?.withAction === true) {
          filtered.push({ questionKey: key, options: [v] });
        }
      } else if (key.startsWith("many_")) {
        const arr = Array.isArray(value) ? (value as any[]) : [];
        const hits = arr.filter((item) => {
          const opt = item?.value ?? item?.rest?.value;
          return opt?.withAction === true;
        });
        if (hits.length) {
          filtered.push({ questionKey: key, options: hits });
        }
      }
    }

    if (filtered.length) {
      // eslint-disable-next-line no-console
      console.log("action-required filtered:", filtered);
      return true;
    }
    return false;
  }

  // Build questions from stored Test payload if possible
  const questions: QuestionItem[] = useMemo(() => {
    try {
      const raw = typeof Test === "string" ? JSON.parse(Test) : Test;
      if (Array.isArray(raw)) {
        return raw
          .filter((q: any) => q && typeof q === "object")
          .map((q: any) => ({
            name: String(q.name ?? q.title ?? q.key ?? Math.random()),
            type: q.type as QuestionItem["type"],
            isRequired: Boolean(q.isRequired ?? q.require),
            options: Array.isArray(q.options)
              ? q.options.map((o: any) => ({
                  id: String(o.id),
                  name: String(o.name),
                  isCorrect: !!o.isCorrect,
                  withAction: !!o.withAction,
                }))
              : [],
            value: q.value,
          })) as QuestionItem[];
      }
    } catch (e) {
      // ignore malformed Test
    }
    return [];
  }, [Test]);

  async function toggleCompleted(type: "done" | "deviation", state: boolean) {
    const answers = form.getValues("answers");
    // Preview payload for saving

    const answersToSave = JSON.stringify(answers);

    const status = getStatus(type, state, Result.isDone, Result.hasDeviation);
    try {
      await updateTaskStatus(status, item, currentUser, periodDate, answersToSave);
      // Оптимистично обновляем локальное состояние ответов,
      // чтобы при повторном открытии карточки ответы были загружены без перезагрузки страницы
      changeTask("Answers", answers);
    } catch (error) {
      console.log("error toggleCompleted:>> ", error);
    } finally {
    }

    if (type === "done") {
      changeTask("Result", { ...Result, isDone: !Result.isDone });
      // Reset unsaved changes after successful save
      setHasUnsavedChanges(false);
    } else if (type === "deviation") {
      changeTask("Result", { ...Result, hasDeviation: !Result.hasDeviation });
    }
  }

  // Handler for questionnaire changes
  function handleQuestionnaireChange(updatedAnswers: Record<string, unknown>) {
    // form.setValue("answers", updatedAnswers, { shouldDirty: true });

    // Only track changes if task is completed
    if (item.Result.isDone) {
      // Compare with saved answers to detect real changes immediately
      const currentJson = JSON.stringify(updatedAnswers);
      const savedJson = JSON.stringify(savedAnswers);
      const hasChanges = currentJson !== savedJson;

      setHasUnsavedChanges(hasChanges);
    }
  }

  // Cancel changes and restore original answers
  function handleCancelChanges() {
    setHasUnsavedChanges(false);
    setResetKey((prev) => prev + 1); // Force full remount with fresh data
  }

  // Save changes for completed task
  async function handleSaveChanges() {
    const answersToSave = form.getValues("answers");
    const answersJson = JSON.stringify(answersToSave);

    try {
      await updateTaskStatus(item.Status ?? "Не выполнено", item, currentUser, periodDate, answersJson);
      // Update parent state
      changeTask("Answers", answersToSave);
      setHasUnsavedChanges(false);
      // eslint-disable-next-line no-console
      console.log("Changes saved successfully:", answersToSave);
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  }

  async function removeTask(task: TaskItem) {
    try {
      const connectionID = await updateItemRequest({
        listName: myTaskCheckListConnectionWithTasksListName,
        body: {},
        bodyArr: [{ Title: "Задача удалена", ID: +task.ConnectionID, EndDate: new Date() }],
        update: true,
      });

      if (connectionID) {
        dispatch(updateItemSuccess({ listName: myTaskCheckListConnectionWithTasksListName, ID: connectionID }));
      }
    } catch (error) {
      console.log("error removeTask:>> ", error);
    } finally {
      setOpenRemoveModal(false);
      toggleOpen(false);
    }
  }

  const isOptional = useMemo(() => {
    return ResponsibleEmail !== "all";
  }, [ResponsibleEmail]);

  const updateTaskItem = useAppSelector((state: RootState) =>
    selectJustUpdatedItem(state, myTaskCheckListTasksListName),
  );

  useAsyncEffect(async () => {
    if (updateTaskItem && item.ID === updateTaskItem) {
      const taskResponse = await getItemsRequest({
        listName: myTaskCheckListTasksListName,
        filters: { ID: item.ID },
      });

      const task = taskResponse.items[0];
      let parsedTest: any[] = [];
      try {
        parsedTest = task?.Test ? JSON.parse(task.Test) : [];
      } catch (e) {
        parsedTest = [];
      }
      // Обновляем локальный item, чтобы пересчитались questions (зависит от Test)
      changeTask("Test", parsedTest);
      // Если используется RHF для локального отображения — обновим и форму
      form.setValue("additionalFields", parsedTest);
    }
  }, [updateTaskItem]);

  // useEffect(() => {
  //   getItemsRequest({
  //     listName: myTaskCheckListConnectionWithTasksListName,
  //     filters: { ResponsibleEmail: currentUser.email === ResponsibleEmail ? ["all*", currentUser.email] : ["all*"] },
  //     populateFields: ["Task"],
  //   }).then((results) => {
  //     const { items } = results;

  //     const modifiedTasks = modifiedItemsState(items, myTaskCheckListConnectionWithTasksListName);

  //     const newTasks = modifiedTasks.reduce((acc: TaskType[], task) => {
  //       if (!task.Task) return acc;

  //       if (acc.some((t) => t.ID === task.Task.ID)) return acc;

  //       const newTask: TaskType = {
  //         ID: task.Task.ID,
  //         Title: task.Task.Title,
  //         Test: task.Task.Metadata?.Test || "",
  //         Direction: task.Task.Metadata?.Direction?.Title || "",
  //         Description: task.Task.Metadata?.Description || "",
  //       };

  //       acc.push(newTask);

  //       return acc;
  //     }, []);

  //     setTasks(newTasks);
  //   });
  // }, [currentUser.email, ResponsibleEmail]);

  useAsyncEffect(async () => {
    if (!hasAddedAction) {
      try {
        const response = await getItemsRequest({
          listName: myTaskActionsListName,
          filters: {
            ImportantHashTag: `#чек-лист_${item.ConnectionID}`,
            CreateActionDate: {
              start: moment(periodDate?.startDate).toISOString(),
              end: moment(periodDate?.endDate).toISOString(),
            },
          },
        });
        const { items } = response;
        if (items.length > 0) {
          setHasAddedAction(true);
        } else {
          setHasAddedAction(false);
        }
      } catch (error) {}
    }
  }, [item.ConnectionID, item.ID, item.Result.isDone, periodDate?.startDate, item.Result.hasDeviation]);

  const hasPercentQuestions = questions.some(
    (question) => question.type === "single" || question.type === "many" || question.type === "grade",
  );

  const actionPeriod = useMemo(
    () => getStartEndDateByPeriod(Period, periodDate?.startDate || new Date()),
    [Period, periodDate?.startDate],
  );

  return (
    <FormProvider {...form}>
      <header className="flex flex-col items-start justify-between p-[24px] text-black border border-[#e5e7eb]">
        <FlexRow className="w-full justify-between items-center leading-none">
          <h1 className="text-[16px]">{Task.Title}</h1>
          <ClosePanelBtnMobile hideXMarkIcon={false} toggleDrawer={toggleOpen} />
        </FlexRow>
        <FlexCol>
          <FlexRow className="items-center pt-2">
            <FlexCol>
              {Result.isDone ? (
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <circle cx="5" cy="5" r="5" fill="#22AD5C" />
                  </svg>
                  &nbsp;&nbsp;
                  <span className={`text-[#22AD5C]`}>Выполнено&nbsp;&nbsp;</span>
                </span>
              ) : (
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <circle cx="5" cy="5" r="5" fill="#BA0C2F" />
                  </svg>
                  &nbsp;&nbsp;
                  <span className={`text-[#BA0C2F]`}>Не выполнено&nbsp;&nbsp;</span>
                </span>
              )}
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </header>
      <main className="p-[24px] space-y-[16px]">
        {isOptional && (
          <div className="space-y-2.5">
            <h4 className="font-normal text-primaryGray">Вид задачи</h4>
            <p className="text-base font-medium text-black">Опциональная задача</p>
          </div>
        )}
        <div className="space-y-2.5">
          <h4 className="font-normal text-primaryGray">Направление</h4>
          <p className="text-base font-medium text-black">{Direction}</p>
        </div>
        {Task.Description && (
          <div className="space-y-2.5">
            <h4 className="font-normal text-primaryGray">Описание</h4>
            <p className="text-base font-medium text-black whitespace-pre-line">{Task.Description}</p>
          </div>
        )}
        {/* Опросник */}
        {item.Test.length > 0 && (
          <>
            <FlexRow className="justify-between items-center">
              <FlexCol>
                <span className="text-lg font-medium">Опросник</span>
              </FlexCol>
              {hasPercentQuestions && (
                <FlexCol className="flex items-center">
                  <span className="inline-block text-lg font-medium"> {Math.round(progressWatch || 0)} / 100%</span>
                </FlexCol>
              )}
            </FlexRow>

            <TGQuestionnaireRHF
              key={`questionnaire-${item.ID}-${resetKey}`}
              questions={questions}
              answers={savedAnswers}
              disabled={effectiveDisabled}
              onChange={handleQuestionnaireChange}
              hasAddedAction={!hasAddedAction}
            />
          </>
        )}
        <div className="space-y-2.5">
          <h3 className="font-medium">Отклонение</h3>
          <div className="w-full p-3.5 shadow-custom rounded-md">
            <TGCheckbox
              disabled={effectiveDisabled}
              checkboxClass={twMerge(effectiveDisabled ? "text-darkblue bg-gray3" : "")}
              isChecked={Result.hasDeviation}
              onChange={() => {
                toggleCompleted("deviation", !Result.hasDeviation);
              }}
            >
              Выявлено отклонение
            </TGCheckbox>
          </div>
        </div>
        <Comments
          history={false}
          zIndex={zIndex + 50}
          IDCard={Number(item.ID)}
          listComments={myTaskCheckListCommentsListName}
          listFilesComments={myTaskCheckListCommentsFilesListName}
          strapiParams={MTCheckListCommentsFiles}
          field="Task"
          disabled={effectiveDisabled}
          onlyCurrentUser
          visiblePeriod={actionPeriod}
          variant="inline"
        />
        <div className="space-y-2.5 ">
          <h3 className="font-medium">Действия</h3>
          <div className="w-full p-3.5 shadow-custom rounded-md">
            <AddActionInComponent
              idModule={Number(item.ConnectionID)}
              typeModule={`чек-лист`}
              CustomStyles="shadow-none task_table"
              viewMode={effectiveDisabled}
              filters={{
                CreateActionDate: actionPeriod,
                AuthorEmail: currentUser.email.toLowerCase().trim(),
              }}
              hasAddAction={(state: boolean) => {
                setHasAddedAction(state);
              }}
              merge={false}
            />
          </div>
        </div>
      </main>
      <footer className="flex flex-col items-center justify-between flex-wrap px-[24px] pt-0 pb-[24px] space-y-[8px] text-black">
        {!item.Result.isDone ? (
          <>
            <FlexCol className="w-full">
              <FlexCol className="gap-3">
                <CreateTaskRelationsButton zIndex={zIndex} disabled={effectiveDisabled} />
                {!item.isDeleted && canManageTask && (
                  <TGButton
                    type="button"
                    icon={<TrashIcon width={20} height={20} />}
                    color="white"
                    disabled={readOnly}
                    onClick={() => {
                      if (readOnly) return;
                      setOpenRemoveModal(true);
                    }}
                  >
                    Удалить задачу
                  </TGButton>
                )}
                {/* {isEditable && (
                  <TGButton
                    type="button"
                    icon={<PencilIcon width={20} height={20} />}
                    color="white"
                    onClick={() => setOpenEditModal(true)}
                  >
                    Изменить задачу
                  </TGButton>
                )} */}
                {!effectiveDisabled && (
                  <TGButton
                    className="w-full"
                    color="bluedark"
                    onClick={() => {
                      if (!hasAddedAction && checkActionsByQuestion()) {
                        setOpenActionRequiredModal(true);
                      } else {
                        setOpenCompletedModal(true);
                      }
                    }}
                  >
                    Задача выполнена
                  </TGButton>
                )}
              </FlexCol>
            </FlexCol>
            <TGButton
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                toggleOpen(false);
              }}
            >
              Закрыть
            </TGButton>
          </>
        ) : (
          <>
            {/* Задача выполнена (isDone = true) */}
            {!hasUnsavedChanges ? (
              // Показываем только кнопку "Закрыть", если нет изменений

              <TGButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleOpen(false);
                }}
              >
                Закрыть
              </TGButton>
            ) : (
              // Показываем кнопки "Отменить" и "Сохранить", если есть изменения
              <>
                <TGButton
                  type="button"
                  color="white"
                  onClick={() => {
                    handleCancelChanges();
                  }}
                >
                  Отменить изменения
                </TGButton>
                <TGButton
                  type="button"
                  color="bluedark"
                  onClick={() => {
                    if (!hasAddedAction && checkActionsByQuestion()) {
                      setOpenActionRequiredModal(true);
                    } else {
                      handleSaveChanges();
                    }
                  }}
                >
                  Сохранить изменения
                </TGButton>
              </>
            )}
          </>
        )}
        {/* <TGButton
          type="button"
          icon={<TrashIcon width={20} height={20} />}
          color="white"
          onClick={() => {
            setOpenRemoveModal(true);
          }}
        >
          Удалить задачу
        </TGButton> */}
      </footer>

      {/* Модальное окно "изменение задачи" */}
      {/* {openEditModal && (
        <DrawerComponent percentage="87" zIndex={1001} open={true} toggleDrawer={(t) => setOpenEditModal(t)}>
          <TaskFormEdit
            item={item}
            email={item.ResponsibleEmail}
            tasks={tasks}
            defaultValues={defaultValues}
            selectedTask={selectedTask}
            setSelectedTask={setSelectedTask}
            toggleFormModal={() => {
              setOpenEditModal(false);
              // toggleFormModal("close");
              // toggleModal(false);
            }}
          />
        </DrawerComponent>
      )} */}

      {/* Модальное окно "невозможно выполнить задачу" */}
      <Modal
        zIndex={120}
        open={openActionRequiredModal}
        toggleDialog={(state) => setOpenActionRequiredModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Невозможно выполнить задачу</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>
            Вы указали ответ, который требует создание действия. <br />
          </p>
        </div>

        {!effectiveDisabled ? (
          <footer className="flex justify-center px-10 pt-10 mt-4 border-t">
            <TGButton type="button" onClick={() => setOpenActionRequiredModal(false)}>
              Закрыть
            </TGButton>
          </footer>
        ) : null}
      </Modal>

      {/* Модальное окно "удаление задачи" */}
      <Modal
        zIndex={120}
        open={openRemoveModal}
        toggleDialog={(state) => setOpenRemoveModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Подтвердите действие</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>Вы уверены, что хотите удалить эту задачу из чек-листа?</p>
          <p>После удаления задачи пропадут все результаты выполнения задачи</p>
          <p>Это действие нельзя будет отменить</p>
        </div>

        {!readOnly ? (
          <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
            <TGButton type="button" onClick={() => setOpenRemoveModal(false)}>
              Отмена
            </TGButton>
            <TGButton
              type="button"
              color="bluedark"
              onClick={() => {
                removeTask(item);
              }}
            >
              Удалить
            </TGButton>
          </footer>
        ) : null}
      </Modal>

      {/* Модальное окно "выполнение этой задачи" */}
      <Modal
        zIndex={120}
        open={openCompletedModal}
        toggleDialog={(state) => setOpenCompletedModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Подтвердите действие</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>
            Вы подтверждаете выполнение этой задачи? <br />
          </p>
          <p>
            После{" "}
            {moment(getStartEndDateByPeriod(Period, periodDate?.startDate).end).add(1, "week").format("DD.MM.YYYY")} ее
            нельзя будет изменить
          </p>
        </div>

        {!effectiveDisabled ? (
          <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
            <TGButton type="button" onClick={() => setOpenCompletedModal(false)}>
              Отмена
            </TGButton>
            <TGButton
              type="button"
              color="bluedark"
              onClick={() => {
                toggleCompleted("done", !Result.isDone);
                setOpenCompletedModal(false);
                toggleOpen(false);
              }}
            >
              Подтвердить
            </TGButton>
          </footer>
        ) : null}
      </Modal>
    </FormProvider>
  );
}

// Функция для получения ключа ответа (как в TGQuestionnaire)
// Может понадобиться для отображения заполненных ответов в будущем
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getAnswerKey(q: QuestionItem, index: number): string {
  const trimmed = (q.id || "").trim();
  return trimmed ? trimmed : `${q.type}_${index}`;
}

// Функция для парсинга вопросов из Test
function parseQuestions(test: string | any[] | undefined | null): QuestionItem[] {
  try {
    // Обрабатываем пустые значения
    if (!test) return [];

    // Если это уже массив, используем его напрямую
    if (Array.isArray(test)) {
      return test
        .filter((q: any) => q && typeof q === "object")
        .map((q: any) => ({
          name: String(q.name ?? q.title ?? q.key ?? Math.random()),
          type: q.type as QuestionItem["type"],
          isRequired: Boolean(q.isRequired ?? q.require),
          options: Array.isArray(q.options)
            ? q.options.map((o: any) => ({
                id: String(o.id),
                name: String(o.name),
                isCorrect: !!o.isCorrect,
                withAction: !!o.withAction,
              }))
            : [],
          value: q.value,
          id: q.id ? String(q.id) : undefined,
        })) as QuestionItem[];
    }

    // Если это строка, пытаемся распарсить
    if (typeof test === "string") {
      // Проверяем на пустую строку
      if (test.trim() === "" || test.trim() === "[]") return [];

      const raw = JSON.parse(test);
      if (Array.isArray(raw)) {
        return raw
          .filter((q: any) => q && typeof q === "object")
          .map((q: any) => ({
            name: String(q.name ?? q.title ?? q.key ?? Math.random()),
            type: q.type as QuestionItem["type"],
            isRequired: Boolean(q.isRequired ?? q.require),
            options: Array.isArray(q.options)
              ? q.options.map((o: any) => ({
                  id: String(o.id),
                  name: String(o.name),
                  isCorrect: !!o.isCorrect,
                  withAction: !!o.withAction,
                }))
              : [],
            value: q.value,
            id: q.id ? String(q.id) : undefined,
          })) as QuestionItem[];
      }
    }
  } catch (e) {
    console.error("Failed to parse questions:", e, "Test value:", test);
  }
  return [];
}

// Функция для парсинга ответов
// Может понадобиться для отображения заполненных ответов в будущем
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function parseAnswers(answers: any): Record<string, unknown> {
  try {
    if (!answers) return {};
    if (typeof answers === "string") return JSON.parse(answers);
    if (typeof answers === "object") return answers;
    return {};
  } catch (e) {
    console.error("Failed to parse answers:", e);
    return {};
  }
}

// Функция для форматирования ответа в читаемый текст
// Может понадобиться для отображения заполненных ответов в будущем
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function formatAnswerValue(answer: unknown, question: QuestionItem): string {
  if (answer === undefined || answer === null) return "";

  switch (question.type) {
    case "single": {
      if (typeof answer === "string") return answer;
      if (answer && typeof answer === "object") {
        const raw = answer as any;
        return String(raw.title ?? raw.name ?? raw.id ?? "");
      }
      return "";
    }
    case "many": {
      if (!Array.isArray(answer)) return "";
      return answer
        .map((item: any) => {
          if (typeof item === "string") return item;
          return String(item?.title ?? item?.name ?? item?.id ?? "");
        })
        .filter((v: string) => v)
        .join(", ");
    }
    case "location":
    case "department":
    case "sector":
    case "employee": {
      if (!Array.isArray(answer)) return "";
      return answer
        .map((item: any) => String(item?.name ?? item?.title ?? ""))
        .filter((v: string) => v)
        .join(", ");
    }
    case "date": {
      if (typeof answer === "string") {
        try {
          const date = new Date(answer);
          return moment(date).format("DD.MM.YYYY");
        } catch {
          return answer;
        }
      }
      return String(answer);
    }
    case "free": {
      return String(answer);
    }
    case "grade": {
      return String(answer);
    }
    default:
      return String(answer);
  }
}

// Функция для получения инструкции по типу вопроса
function getQuestionInstruction(question: QuestionItem): string {
  switch (question.type) {
    case "single":
      return "Выберите 1 ответ";
    case "many":
      return "Выберите 1 или несколько ответов";
    case "location":
      return "Введите локацию";
    case "department":
      return "Введите отдел";
    case "sector":
      return "Введите участок";
    case "employee":
      return "Введите сотрудника";
    case "date":
      return "Введите дату";
    case "free":
      return "Введите свой ответ";
    case "grade":
      return "Введите оценку";
    default:
      return "Заполните поле";
  }
}

const QUESTION_TEXT_COLUMN_SPAN = 5;
const QUESTION_TEXT_CHAR_PER_LINE = 85;
const QUESTION_TEXT_LINE_HEIGHT = 16;

function expandRowForQuestion(
  worksheet: ExcelJS.Worksheet,
  row: ExcelJS.Row,
  text?: string,
  minHeight = QUESTION_TEXT_LINE_HEIGHT,
) {
  worksheet.mergeCells(row.number, 1, row.number, QUESTION_TEXT_COLUMN_SPAN);

  if (!text && !minHeight) {
    return;
  }

  const normalizedText = text?.toString().trim() ?? "";
  if (normalizedText.length === 0) {
    row.height = minHeight;
    return;
  }

  const approximateLines = Math.max(1, Math.ceil(normalizedText.length / QUESTION_TEXT_CHAR_PER_LINE));
  row.height = Math.max(minHeight, approximateLines * QUESTION_TEXT_LINE_HEIGHT);
}

function DownloadTaskExcelButton({
  task,
  fileTitle,
  periodDate,
  buttonProps: { children = "", disabled = false, className = "" },
}: {
  task: TaskItem;
  fileTitle: string;
  periodDate: DateType | undefined;
  buttonProps: { children?: ReactNode; disabled?: boolean; className?: string };
}) {
  // Создаем новый workbook и worksheet
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(fileTitle);

  // Устанавливаем ширину колонок
  worksheet.columns = [
    { header: "", key: "title", width: 20 },
    { header: "", key: "value", width: 50 },
  ];

  for (let colIdx = 3; colIdx <= QUESTION_TEXT_COLUMN_SPAN; colIdx += 1) {
    const column = worksheet.getColumn(colIdx);
    if (!column.width || column.width < 18) {
      column.width = 18;
    }
  }

  // Добавляем данные в вертикальную структуру (метаданные задачи)
  worksheet.addRow(["Название", task.Task.Title]);
  worksheet.addRow([]);
  worksheet.addRow(["Направление", task.Direction]);
  worksheet.addRow([]);
  worksheet.addRow(["Описание", task.Task.Description]);
  worksheet.addRow([]);
  worksheet.addRow(["Критерий выполнения", task.Task.CompletionCriteria]);
  worksheet.addRow([]);
  worksheet.addRow(["Период", getTaskPeriodString(task.Period, periodDate)]);

  // Парсим вопросы и ответы
  // Обрабатываем случай, когда Test может быть строкой или массивом
  const questions = parseQuestions(task.Test);

  // Добавляем опросник, если есть вопросы
  if (questions.length > 0) {
    // Пустая строка перед опросником
    worksheet.addRow([]);
    worksheet.addRow([]);

    // Заголовок опросника с серым фоном
    const questionnaireHeaderRow = worksheet.addRow(["Опросник", ""]);
    const headerCell = questionnaireHeaderRow.getCell(1);
    headerCell.font = { bold: true };
    headerCell.alignment = { horizontal: "left", vertical: "middle" };
    headerCell.style.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "EFEFEF" },
    };

    worksheet.addRow([]);
    const generalInstructionText =
      "Заполните опросник. Выделите ячейку с вашим вариантом ответа, либо введите свой ответ";
    const generalInstructionRow = worksheet.addRow([generalInstructionText, "", "", "", ""]);
    expandRowForQuestion(worksheet, generalInstructionRow, generalInstructionText, 24);
    const generalInstructionCell = generalInstructionRow.getCell(1);
    generalInstructionCell.alignment = { horizontal: "left", vertical: "top", wrapText: true };
    generalInstructionCell.font = { italic: true, color: { argb: "a7a7a7" } };
    worksheet.addRow([]);

    // Добавляем каждый вопрос
    questions.forEach((question, index) => {
      const questionNumber = index + 1;
      const instruction = getQuestionInstruction(question);

      // Номер вопроса и название
      const questionTitle = `${questionNumber}. ${question.name}`;
      const questionRow = worksheet.addRow([questionTitle, "", "", ""]);
      expandRowForQuestion(worksheet, questionRow, questionTitle);
      const questionCell = questionRow.getCell(1);
      questionCell.font = { bold: true };
      questionCell.alignment = { horizontal: "left", vertical: "top", wrapText: true };

      // Инструкция курсивом в первой колонке
      const instructionRow = worksheet.addRow([instruction, "", "", ""]);
      expandRowForQuestion(worksheet, instructionRow, instruction);
      const instructionCell = instructionRow.getCell(1);
      instructionCell.font = { italic: true, color: { argb: "a7a7a7" } };
      instructionCell.alignment = { horizontal: "left", vertical: "top", wrapText: true };

      // Варианты ответов (если есть) в первой колонке
      if (question.options && question.options.length > 0) {
        question.options.forEach((option) => {
          const optionRow = worksheet.addRow([option.name, "", "", ""]);
          const optionCell = optionRow.getCell(1);
          optionCell.alignment = { horizontal: "left", vertical: "top", wrapText: true };
        });
      } else {
        // Для типов вопросов без вариантов (location, department, sector, employee, free, date)
        // создаем объединенные 3 ячейки для ввода ответа
        const answerRow = worksheet.addRow(["", "", "", ""]);
        expandRowForQuestion(worksheet, answerRow, "", 24);
        const answerCell = answerRow.getCell(1);
        answerCell.alignment = { horizontal: "left", vertical: "top", wrapText: true };
      }

      // Пустая строка между вопросами
      worksheet.addRow([]);
    });
  }

  // Стилизация метаданных задачи
  worksheet.getColumn(1).eachCell((cell, rowNumber) => {
    if (rowNumber % 2 === 0 && rowNumber <= 10) {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "middle" };
      cell.style.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "EFEFEF" },
      };
    }
  });

  // Стилизация всех строк
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber > 1) {
      row.eachCell((cell, colNumber) => {
        cell.alignment = {
          vertical: "top",
          horizontal: "left",
          wrapText: true,
          indent: 0.2,
        };
      });
    }
  });

  // Сохранение файла
  const fileName = `${fileTitle}.xlsx`;

  function getExcelFile() {
    workbook.xlsx.writeBuffer().then(function (data) {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const linkDownLoad = document.createElement("a");
      linkDownLoad.setAttribute("download", fileName);
      linkDownLoad.setAttribute("href", url);
      linkDownLoad.style.display = "none";
      document.body.appendChild(linkDownLoad);
      linkDownLoad.click();
      document.body.removeChild(linkDownLoad);
      window.URL.revokeObjectURL(url);
    });
  }

  return (
    <TGButton className={className} disabled={disabled} onClick={getExcelFile}>
      {children}
    </TGButton>
  );
}

export function TaskFormEdit({
  item,
  defaultValues,
  selectedTask,
  setSelectedTask,
  toggleFormModal,
  email,
}: {
  item: TaskItem;
  tasks: ListFieldsType[typeof myTaskCheckListTasksListName][];
  defaultValues: TaskFormValues;
  selectedTask: Option[];
  setSelectedTask: (value: Option[]) => void;
  toggleFormModal: (value: boolean) => void;
  email: string;
}) {
  const users: any = useAppSelector(selectUsers);
  const dispatch = useAppDispatch();
  const form = useForm<TaskFormValues>({
    defaultValues: defaultValues,
  });

  const [customLoading, setCustomLoading] = useState(false);

  const [taskTitle, setTaskTitle] = useState("");

  const onSubmit = async (data: TaskFormValues) => {
    setCustomLoading(true);

    try {
      await updateItemRequest({
        listName: myTaskCheckListTasksListName,
        body: {
          ID: item.ID,
          Test: JSON.stringify(data.additionalFields),
        },
      });

      dispatch(updateItemSuccess({ listName: myTaskCheckListTasksListName, ID: +selectedTask[0].id }));
    } catch (error) {
      console.error(error, (error as Error).message || "Неизвестная ошибка");
    } finally {
      setCustomLoading(false);
      toggleFormModal(false);
    }
  };

  useEffect(() => {
    async function fetchTask() {
      if (selectedTask.length > 0) {
        const task = await getTaskByID(selectedTask[0].id);
        setTaskTitle(task.Title);

        const connectionResponse = await getItemsRequest({
          listName: myTaskCheckListConnectionWithTasksListName,
          filters: { Task: selectedTask[0].id },
        });

        const connection = connectionResponse.items[0];

        if (task) {
          form.setValue("description", task.Description);
          form.setValue("direction", [
            { id: task.Direction.ID.toString(), name: task.Direction.Title, value: task.Direction },
          ]);
          form.setValue("task", task.Title);
        }

        form.setValue("frequency", [
          { id: connection.TaskPeriod, name: connection.TaskPeriod, value: connection.TaskPeriod },
        ]);
      }
    }
    fetchTask();
  }, [selectedTask[0]?.id]);

  return (
    <FlexCol>
      {(customLoading && (
        <FlexCol className="px-7.5 py-5 justify-center items-center gap-5 w-full h-full">
          <span className="text-base text-darkblue">Изменяем задачу...</span>
          <Loading />
        </FlexCol>
      )) || (
        <>
          <FlexRow className="flex px-10 pt-10 pb-8">
            <h1 className="text-black">{taskTitle ?? "Опциональная задача"}</h1>
          </FlexRow>
          <FormProvider {...form}>
            <form
              className="flex flex-row flex-wrap px-10 custom-selects"
              onSubmit={form.handleSubmit((data) => onSubmit(data))}
            >
              <FlexCol className="gap-y-5 w-full">
                <FlexRow>
                  <div className="space-y-2.5">
                    <h4 className="font-normal text-primaryGray">Задача</h4>
                    <p className="text-base font-medium text-black">{form.watch("direction")[0]?.name ?? ""}</p>
                  </div>
                </FlexRow>
                <FlexRow>
                  <div className="space-y-2.5">
                    <h4 className="font-normal text-primaryGray">Направление</h4>
                    <p className="text-base font-medium text-black">{form.watch("direction")[0]?.name ?? ""}</p>
                  </div>
                </FlexRow>
                <FlexRow>
                  <div className="space-y-2.5">
                    <h4 className="font-normal text-primaryGray">Описание</h4>
                    <p className="text-base font-medium text-black">{form.watch("description") ?? ""}</p>
                  </div>
                </FlexRow>
                <FlexRow>
                  <div className="space-y-2.5">
                    <h4 className="font-normal text-primaryGray">Должность</h4>
                    <p className="text-base font-medium text-black">{users[email].jobTitleStr ?? ""}</p>
                  </div>
                </FlexRow>
              </FlexCol>

              <FlexCol className="w-full">
                <h4 className="my-5 font-normal text-primaryGray">Опросник</h4>
                <AdditionalFieldsProvider>
                  <CheckListAdditionalMeetingFields />
                </AdditionalFieldsProvider>
              </FlexCol>
              <footer className="flex justify-between py-10 w-full shrink-0">
                <TGButton type="button" onClick={() => toggleFormModal(false)}>
                  Назад
                </TGButton>
                <TGButton type="submit" disabled={!form.formState.isDirty} color="bluedark">
                  Изменить задачу в чек-листе
                </TGButton>
              </footer>
            </form>
          </FormProvider>
        </>
      )}
    </FlexCol>
  );
}
